import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  faChevronLeft,
  faChevronRight,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AccountingDayRoutingAction } from "actions/accountingDayRouting";

import { Button, SecondaryButton } from "components/Form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import SearchInput from "components/SearchInput";

import {
  getAccountingDayRoutingById,
  getBusinesses,
  getCurrentSale,
  getDefaultBillingSettings,
  getIsFetchingAccountingDayRouting,
  getPayments,
  getSaleLots,
} from "selectors";

import toast from "lib/toast";

import { BuyerColumn } from "./BuyerColumn";
import {
  buildConfirmPayload,
  countBuyersNeedingReview,
  countPendingSuggestions,
  filterAndSortSuggestions,
  findSaleLotsForBuyer,
  resolveBuyerDestination,
  RoutingSide,
  summarizeBuyerSaleLots,
} from "./routing";
import {
  Columns,
  FooterNote,
  MoveButton,
  MoveColumn,
  SearchWrapper,
  Subtitle,
  Toolbar,
} from "./styles";

interface AccountingDayRoutingModalProps {
  onClose: () => void;
}

export function AccountingDayRouting({
  onClose,
}: AccountingDayRoutingModalProps) {
  const dispatch = useDispatch();
  const currentSale = useSelector(getCurrentSale);
  const billingSettings = useSelector(getDefaultBillingSettings);
  const suggestionsById = useSelector(getAccountingDayRoutingById);
  const isFetching = useSelector(getIsFetchingAccountingDayRouting);
  const saleLotsById = useSelector(getSaleLots);
  const businessesById = useSelector(getBusinesses);
  const paymentsById = useSelector(getPayments);

  const livestockSaleId = currentSale?.id;
  const cashBuyerBusinessId = billingSettings?.cashBuyerBusinessId || null;

  const [searchText, setSearchText] = useState("");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [suggestionsAccepted, setSuggestionsAccepted] = useState(false);
  const [explicitDestinationByBuyerId, setExplicitDestinationByBuyerId] =
    useState<Record<string, RoutingSide>>({});
  const [isSelectedByBuyerId, setIsSelectedByBuyerId] = useState<
    Record<string, boolean>
  >({});

  const suggestions = useMemo(
    () => Object.values(suggestionsById || {}),
    [suggestionsById],
  );

  const saleLotSummaryByBuyerId = useMemo(
    () =>
      suggestions.reduce((summaries, suggestion) => {
        summaries[suggestion.id] = summarizeBuyerSaleLots(
          findSaleLotsForBuyer(
            saleLotsById,
            livestockSaleId,
            suggestion.id,
            cashBuyerBusinessId,
          ),
          suggestion.id,
          paymentsById,
        );
        return summaries;
      }, {}),
    [
      suggestions,
      saleLotsById,
      livestockSaleId,
      cashBuyerBusinessId,
      paymentsById,
    ],
  );

  const buyerNameById = useMemo(
    () =>
      suggestions.reduce((names, suggestion) => {
        names[suggestion.id] = businessesById?.[suggestion.id]?.name || "";
        return names;
      }, {}),
    [suggestions, businessesById],
  );

  const individualColumnRows = useMemo(
    () =>
      filterAndSortSuggestions(
        suggestions,
        RoutingSide.Individual,
        explicitDestinationByBuyerId,
        suggestionsAccepted,
        searchText,
        showFlaggedOnly,
        buyerNameById,
        saleLotSummaryByBuyerId,
      ),
    [
      suggestions,
      explicitDestinationByBuyerId,
      suggestionsAccepted,
      searchText,
      showFlaggedOnly,
      buyerNameById,
      saleLotSummaryByBuyerId,
    ],
  );

  const cashAccountColumnRows = useMemo(
    () =>
      filterAndSortSuggestions(
        suggestions,
        RoutingSide.Cash,
        explicitDestinationByBuyerId,
        suggestionsAccepted,
        searchText,
        showFlaggedOnly,
        buyerNameById,
        saleLotSummaryByBuyerId,
      ),
    [
      suggestions,
      explicitDestinationByBuyerId,
      suggestionsAccepted,
      searchText,
      showFlaggedOnly,
      buyerNameById,
      saleLotSummaryByBuyerId,
    ],
  );

  const pendingSuggestions = useMemo(
    () =>
      countPendingSuggestions(
        suggestions,
        explicitDestinationByBuyerId,
        suggestionsAccepted,
      ),
    [suggestions, explicitDestinationByBuyerId, suggestionsAccepted],
  );

  const buyersNeedingReviewCount = useMemo(
    () =>
      countBuyersNeedingReview(
        suggestions,
        explicitDestinationByBuyerId,
        suggestionsAccepted,
      ),
    [suggestions, explicitDestinationByBuyerId, suggestionsAccepted],
  );

  useEffect(() => {
    if (!livestockSaleId) return undefined;

    dispatch(AccountingDayRoutingAction.request({ livestockSaleId }));
    return () => {
      dispatch(AccountingDayRoutingAction.reset());
    };
  }, [dispatch, livestockSaleId]);

  const moveBuyersToSide = (buyerIds: string[], side: RoutingSide) => {
    if (buyerIds.length === 0) {
      return;
    }

    setExplicitDestinationByBuyerId(previousDestinations => {
      const nextDestinations = { ...previousDestinations };
      buyerIds.forEach(buyerId => {
        nextDestinations[buyerId] = side;
      });
      return nextDestinations;
    });

    setIsSelectedByBuyerId({});
  };

  const toggleBuyerSelected = (buyerId: string) => {
    setIsSelectedByBuyerId(previousSelection => {
      const nextSelection = { ...previousSelection };
      if (nextSelection[buyerId]) {
        delete nextSelection[buyerId];
      } else {
        nextSelection[buyerId] = true;
      }
      return nextSelection;
    });
  };

  const toggleSelectAllInRows = (rows: AccountingDayRoutingSuggestion[]) => {
    setIsSelectedByBuyerId(previousSelection => {
      const nextSelection = { ...previousSelection };
      const allRowsSelected =
        rows.length > 0 && rows.every(row => nextSelection[row.id]);

      rows.forEach(row => {
        if (allRowsSelected) {
          delete nextSelection[row.id];
        } else {
          nextSelection[row.id] = true;
        }
      });

      return nextSelection;
    });
  };

  const acceptAllSuggestions = () => {
    if (pendingSuggestions === 0) {
      return;
    }
    // Only apply the algorithmic suggestion to buyers with no explicit
    // decision yet - explicitDestinationByBuyerId must survive this, or a
    // deliberate manual override that disagrees with the suggestion gets
    // silently reverted.
    setSuggestionsAccepted(true);
    setIsSelectedByBuyerId({});
  };

  const handleToggleShowFlaggedOnly = () =>
    setShowFlaggedOnly(previous => !previous);
  const handleMoveIndividualBuyerToCash = (buyerId: string) =>
    moveBuyersToSide([buyerId], RoutingSide.Cash);
  const handleMoveCashBuyerToIndividual = (buyerId: string) =>
    moveBuyersToSide([buyerId], RoutingSide.Individual);

  const selectedIndividualBuyerIds = individualColumnRows
    .filter(row => isSelectedByBuyerId[row.id])
    .map(row => row.id);
  const selectedCashAccountBuyerIds = cashAccountColumnRows
    .filter(row => isSelectedByBuyerId[row.id])
    .map(row => row.id);

  const handleMoveSelectedIndividualBuyersToCash = () =>
    moveBuyersToSide(selectedIndividualBuyerIds, RoutingSide.Cash);
  const handleMoveSelectedCashBuyersToIndividual = () =>
    moveBuyersToSide(selectedCashAccountBuyerIds, RoutingSide.Individual);

  const confirm = () => {
    if (!cashBuyerBusinessId) {
      toast.error("This deployment has no Cash Account business configured.");
      onClose();
      return;
    }

    const routingByBuyerId: Record<string, boolean> = {};
    suggestions.forEach(suggestion => {
      const destination = resolveBuyerDestination(
        suggestion,
        explicitDestinationByBuyerId,
        suggestionsAccepted,
      );
      routingByBuyerId[suggestion.id] = destination === RoutingSide.Cash;
    });

    const { buyerIdsToRouteToCash, buyerIdsToRouteToIndividual } =
      buildConfirmPayload(
        suggestions,
        routingByBuyerId,
        saleLotsById,
        livestockSaleId,
        cashBuyerBusinessId,
      );

    if (
      buyerIdsToRouteToCash.length > 0 ||
      buyerIdsToRouteToIndividual.length > 0
    ) {
      dispatch(
        AccountingDayRoutingAction.confirm({
          livestockSaleId,
          cashBuyerIds: buyerIdsToRouteToCash,
          individualBuyerIds: buyerIdsToRouteToIndividual,
        }),
      );
    } else {
      toast.info("No routing changes to apply.");
    }

    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle onClose={onClose}>Assign Cash Sales</DialogTitle>
      <DialogContent dividers>
        {isFetching && "Loading suggested buyer routing..."}
        {!isFetching && !cashBuyerBusinessId && (
          <p>
            This deployment has no Cash Account business configured. Set one up
            before routing buyers here.
          </p>
        )}
        {!isFetching && cashBuyerBusinessId && suggestions.length === 0 && (
          <p>No buyers are ready for accounting-day review.</p>
        )}
        {!isFetching && cashBuyerBusinessId && suggestions.length > 0 && (
          <>
            <Subtitle>
              Sale {livestockSaleId} &middot; {suggestions.length} buyer
              {suggestions.length === 1 ? "" : "s"}
            </Subtitle>
            <Toolbar>
              <SearchWrapper>
                <SearchInput value={searchText} handleChange={setSearchText} />
              </SearchWrapper>
              {showFlaggedOnly ? (
                <Button
                  type="button"
                  data-tour="accounting-day-needs-review"
                  onClick={handleToggleShowFlaggedOnly}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  &nbsp;Needs review ({buyersNeedingReviewCount})
                </Button>
              ) : (
                <SecondaryButton
                  type="button"
                  data-tour="accounting-day-needs-review"
                  onClick={handleToggleShowFlaggedOnly}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  &nbsp;Needs review ({buyersNeedingReviewCount})
                </SecondaryButton>
              )}
              <SecondaryButton
                type="button"
                data-tour="accounting-day-accept-all"
                disabled={pendingSuggestions === 0}
                onClick={acceptAllSuggestions}
              >
                Accept all suggestions ({pendingSuggestions})
              </SecondaryButton>
            </Toolbar>
            <Columns>
              <BuyerColumn
                title="Individual Invoicing"
                side={RoutingSide.Individual}
                rows={individualColumnRows}
                isSelectedByBuyerId={isSelectedByBuyerId}
                onToggleSelected={toggleBuyerSelected}
                onSelectAll={toggleSelectAllInRows}
                onMoveRow={handleMoveIndividualBuyerToCash}
                saleLotSummaryByBuyerId={saleLotSummaryByBuyerId}
                emptyMessage="No buyers here."
                dataTour="accounting-day-select-all-individual"
              />
              <MoveColumn>
                <MoveButton
                  type="button"
                  title="Move to Cash Account"
                  data-tour="accounting-day-move-to-cash"
                  disabled={selectedIndividualBuyerIds.length === 0}
                  onClick={handleMoveSelectedIndividualBuyersToCash}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </MoveButton>
                <MoveButton
                  type="button"
                  title="Move to Individual Invoicing"
                  data-tour="accounting-day-move-to-individual"
                  disabled={selectedCashAccountBuyerIds.length === 0}
                  onClick={handleMoveSelectedCashBuyersToIndividual}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </MoveButton>
              </MoveColumn>
              <BuyerColumn
                title="Cash Account"
                side={RoutingSide.Cash}
                rows={cashAccountColumnRows}
                isSelectedByBuyerId={isSelectedByBuyerId}
                onToggleSelected={toggleBuyerSelected}
                onSelectAll={toggleSelectAllInRows}
                onMoveRow={handleMoveCashBuyerToIndividual}
                saleLotSummaryByBuyerId={saleLotSummaryByBuyerId}
                emptyMessage="Select buyers on the left and move them here."
                dataTour="accounting-day-select-all-cash"
              />
            </Columns>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <FooterNote>
          {buyersNeedingReviewCount > 0
            ? `${buyersNeedingReviewCount} buyer${buyersNeedingReviewCount === 1 ? "" : "s"} still flagged for the Cash Sales buyer`
            : suggestions.length > 0 && "No buyers need review."}
        </FooterNote>
        <SecondaryButton data-tour="close" type="button" onClick={onClose}>
          Close
        </SecondaryButton>
        <Button
          type="button"
          data-tour="accounting-day-confirm"
          onClick={confirm}
          disabled={
            isFetching || !cashBuyerBusinessId || suggestions.length === 0
          }
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
